import type { SearchOrganizationsError } from '@/generated/http-clients/v3ApiComponents'
import type {
  OrgSearchResponse,
  OrgSearchResult,
} from '@/generated/http-clients/v3ApiSchemas'
import type { UseQueryResult } from '@tanstack/react-query'

export const ORG_SEARCH_LIMIT = 50
export const ORG_SEARCH_MIN_LENGTH = 3
export const EMPTY_VALUE = '—'

const MATCHED_ON_LABEL_KEYS: Record<string, string> = {
  organization_id: 'orgSearch.matchedOn.organization_id',
  billing_entity: 'orgSearch.matchedOn.billing_entity',
  salesforce_account_id: 'orgSearch.matchedOn.salesforce_account_id',
  owner: 'orgSearch.matchedOn.owner',
  member: 'orgSearch.matchedOn.member',
  name: 'orgSearch.matchedOn.name',
  owner_email: 'orgSearch.matchedOn.owner_email',
  member_email: 'orgSearch.matchedOn.member_email',
}

const CREATED_AT_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

export type OrgSearchState =
  | { kind: 'idle' }
  | { kind: 'tooShort' }
  | { kind: 'loading' }
  | { kind: 'results'; results: OrgSearchResult[]; truncated: boolean }
  | { kind: 'empty' }
  | { kind: 'invalid'; message?: string }
  | { kind: 'disabled' }
  | { kind: 'error'; message?: string }

type OrgSearchQuery = Pick<
  UseQueryResult<OrgSearchResponse, SearchOrganizationsError>,
  'data' | 'isError' | 'isLoading'
>

export function deriveOrgSearchState(
  term: string,
  search: OrgSearchQuery,
): OrgSearchState {
  if (term === '') {
    return { kind: 'idle' }
  }
  if (term.length < ORG_SEARCH_MIN_LENGTH) {
    return { kind: 'tooShort' }
  }
  if (search.isError) {
    return { kind: 'error' }
  }
  if (search.isLoading || !search.data) {
    return { kind: 'loading' }
  }
  return stateFromResponse(search.data)
}

function stateFromResponse(response: OrgSearchResponse): OrgSearchState {
  switch (response.outcome) {
    case 'ok':
      if (response.results.length === 0) {
        return { kind: 'empty' }
      }
      return {
        kind: 'results',
        results: response.results,
        truncated: response.truncated,
      }
    case 'invalid':
      return { kind: 'invalid', message: response.message }
    case 'account_service_disabled':
      return { kind: 'disabled' }
    case 'error':
      return { kind: 'error', message: response.message }
    default:
      return { kind: 'error' }
  }
}

export function matchedOnLabelKey(value: string): string | undefined {
  if (!Object.hasOwn(MATCHED_ON_LABEL_KEYS, value)) {
    return undefined
  }
  return MATCHED_ON_LABEL_KEYS[value]
}

export function distinctMatchedOn(matchedOn: string[]): string[] {
  return [...new Set(matchedOn)]
}

export function formatOrgCreatedAt(createdAt: string): string {
  const timestamp = Date.parse(createdAt)
  if (Number.isNaN(timestamp)) {
    return createdAt
  }
  return CREATED_AT_FORMAT.format(timestamp)
}

export function displayValue(value: string | undefined): string {
  if (!value?.trim()) {
    return EMPTY_VALUE
  }
  return value
}
